import { useState } from "react";
import { NavLink } from "react-router";
import { clsx } from "clsx";
import { BookOpen } from "lucide-react";
import { RulesDialog } from "@/features/predictions/components/RulesDialog";
import {
  PREDICTIONS_LEADERBOARD_PATH,
  PREDICTIONS_LOTS_PATH,
  PREDICTIONS_MATCHES_PATH,
  PREDICTIONS_PATH,
  PREDICTIONS_REGLEMENT_PATH,
  PREDICTIONS_SETTINGS_PATH,
  PREDICTIONS_STATS_PATH,
} from "@/features/predictions/constants/paths";
import { useActiveCompetition } from "@/features/predictions/hooks/useActiveCompetition";
import { useCompetitionLots } from "@/features/predictions/hooks/useCompetitionLots";

type TabItem = {
  to: string
  label: string
  end?: boolean
};

const baseClass =
  "inline-flex h-9 items-center rounded-full px-4 text-sm font-semibold transition-colors";

const idleClass =
  "text-gray-700 hover:bg-emerald-50 hover:text-emerald-800 dark:text-gray-300 dark:hover:bg-emerald-950/60 dark:hover:text-emerald-200";

const activeClass =
  "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700";

const rulesButtonClass = clsx(
  baseClass,
  "gap-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-800",
  "dark:text-gray-300 dark:hover:bg-emerald-950/60 dark:hover:text-emerald-200",
);

export const PredictionsTabs = () => {
  const { data: competition } = useActiveCompetition();
  const lotsQuery = useCompetitionLots(competition?.id);
  const [isRulesOpen, setIsRulesOpen] = useState(false);

  const hasLots = !lotsQuery.isLoading && (lotsQuery.data?.length ?? 0) > 0;
  const hasReglement = Boolean(competition?.rules);

  const tabs: TabItem[] = [
    { to: PREDICTIONS_PATH, label: "Tableau de bord", end: true },
    { to: PREDICTIONS_MATCHES_PATH, label: "Matchs" },
    { to: PREDICTIONS_LEADERBOARD_PATH, label: "Classement" },
    ...(hasLots ? [{ to: PREDICTIONS_LOTS_PATH, label: "Lots" }] : []),
    { to: PREDICTIONS_STATS_PATH, label: "Mes stats" },
    ...(hasReglement ? [{ to: PREDICTIONS_REGLEMENT_PATH, label: "Règlement" }] : []),
    { to: PREDICTIONS_SETTINGS_PATH, label: "Réglages" },
  ];

  return (
    <div className="flex items-center justify-between gap-2">
      <nav
        aria-label="Sous-navigation pronostics"
        className="min-w-0 flex-1 overflow-x-auto"
      >
        <ul className="flex gap-2 whitespace-nowrap pb-1">
          {tabs.map((tab) => (
            <li key={tab.to}>
              <NavLink
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  clsx(baseClass, isActive ? activeClass : idleClass)}
              >
                {tab.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      {competition && (
        <>
          <button
            type="button"
            onClick={() => setIsRulesOpen(true)}
            aria-label="Voir les règles du jeu"
            className={rulesButtonClass}
          >
            <BookOpen size={16} aria-hidden />
            <span className="hidden sm:inline">Règles</span>
          </button>
          <RulesDialog open={isRulesOpen} onClose={() => setIsRulesOpen(false)} scoringRules={competition.scoringRules} />
        </>
      )}
    </div>
  );
};

PredictionsTabs.displayName = "PredictionsTabs";
